import { useState, useEffect } from 'react';
import { Plus, Tag, Percent, DollarSign, Trash2, Edit, Check, X, Calendar, Users, Gift, Copy, Download, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

import { API_URL } from '@/config/api';
import VoucherCard, { exportVouchersPDF, downloadSingleVoucherPDF, vouchersPdfBase64, defaultLoginUrl } from '@/components/app/VoucherCard';

export default function AdminPromoCodes() {
  const { token } = useAuth();
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [isVoucherDialogOpen, setIsVoucherDialogOpen] = useState(false);
  const [voucherForm, setVoucherForm] = useState({ value: '100', quantity: '1', note: '', message: '', valid_until: '' });
  const [generatedVouchers, setGeneratedVouchers] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [voucherPdfBusy, setVoucherPdfBusy] = useState(false);
  const [voucherEmail, setVoucherEmail] = useState('');
  const [editingVoucher, setEditingVoucher] = useState(null);
  const [voucherEditForm, setVoucherEditForm] = useState({ discount_value: '', valid_until: '', is_active: true });
  const [referralConfig, setReferralConfig] = useState({ referrer_reward: 15, referee_reward: 0, min_spend: 70, milestone_reward: 70, milestone_threshold: 3 });
  const [savingReferral, setSavingReferral] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_amount: '',
    max_uses: '',
    max_uses_per_user: '1',
    valid_until: ''
  });

  useEffect(() => {
    fetchPromoCodes();
    fetchVouchers();
    fetchReferralConfig();
  }, [token]);

  const fetchPromoCodes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/promo-codes`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPromoCodes(data);
      }
    } catch (error) {
      console.error('Error fetching promo codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVouchers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/vouchers`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) setVouchers(await response.json());
    } catch (error) {
      console.error('Error fetching vouchers:', error);
    }
  };

  const fetchReferralConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/referral-config`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const d = await response.json();
        setReferralConfig({
          referrer_reward: d.referrer_reward ?? 15,
          referee_reward: d.referee_reward ?? 0,
          min_spend: d.min_spend ?? 70,
          milestone_reward: d.milestone_reward ?? 70,
          milestone_threshold: d.milestone_threshold ?? 3
        });
      }
    } catch (error) {
      console.error('Error fetching referral config:', error);
    }
  };

  const handleSaveReferral = async (e) => {
    e.preventDefault();
    setSavingReferral(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/referral-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({
          referrer_reward: parseFloat(referralConfig.referrer_reward) || 0,
          referee_reward: parseFloat(referralConfig.referee_reward) || 0,
          min_spend: parseFloat(referralConfig.min_spend) || 0,
          milestone_reward: parseFloat(referralConfig.milestone_reward) || 0,
          milestone_threshold: parseInt(referralConfig.milestone_threshold) || 0
        })
      });
      if (!response.ok) throw new Error('Failed to save referral settings');
      toast.success('Referral settings saved');
      fetchReferralConfig();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingReferral(false);
    }
  };

  const handleGenerateVouchers = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/vouchers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({
          value: parseFloat(voucherForm.value),
          quantity: parseInt(voucherForm.quantity) || 1,
          note: voucherForm.note,
          message: voucherForm.message,
          valid_until: voucherForm.valid_until || null
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to generate vouchers');
      toast.success(data.message);
      setGeneratedVouchers(data.vouchers || []);
      fetchVouchers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setGenerating(false);
    }
  };

  const vouchersToCards = (list) => list.map(v => ({
    value: Number(v.value) || 0,
    code: v.code,
    message: v.message || voucherForm.message || '',
    expiry: v.valid_until || voucherForm.valid_until || ''
  }));

  const handleDownloadAllVouchers = async () => {
    setVoucherPdfBusy(true);
    try {
      await exportVouchersPDF(vouchersToCards(generatedVouchers), defaultLoginUrl(), 'gc3h-vouchers.pdf');
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('Could not generate PDF');
    } finally {
      setVoucherPdfBusy(false);
    }
  };

  const handleDownloadOneVoucher = async (v) => {
    setVoucherPdfBusy(true);
    try {
      await downloadSingleVoucherPDF(vouchersToCards([v])[0], defaultLoginUrl());
      toast.success('Voucher downloaded');
    } catch (e) {
      toast.error('Could not generate PDF');
    } finally {
      setVoucherPdfBusy(false);
    }
  };

  const handleEmailVouchers = async () => {
    const email = voucherEmail.trim();
    if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    setVoucherPdfBusy(true);
    try {
      const pdf_base64 = await vouchersPdfBase64(vouchersToCards(generatedVouchers), defaultLoginUrl());
      const response = await fetch(`${API_URL}/api/vouchers/email-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ email, pdf_base64, filename: 'gc3h-vouchers.pdf', note: voucherForm.message })
      });
      let data = {};
      try { data = await response.json(); } catch { /* non-JSON (e.g. 413 from proxy) */ }
      if (!response.ok) {
        throw new Error(data.detail || (response.status === 413 ? 'PDF is too large to email — try fewer vouchers per batch, or download instead.' : 'Failed to email PDF'));
      }
      toast.success(data.message || 'Voucher PDF emailed');
      setVoucherEmail('');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setVoucherPdfBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingCode 
        ? `${API_URL}/api/admin/promo-codes/${editingCode.code_id}`
        : `${API_URL}/api/admin/promo-codes`;
      
      const response = await fetch(url, {
        method: editingCode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          discount_value: parseFloat(formData.discount_value),
          min_order_amount: parseFloat(formData.min_order_amount) || 0,
          max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
          max_uses_per_user: parseInt(formData.max_uses_per_user) || 1
        })
      });

      if (response.ok) {
        toast.success(editingCode ? 'Promo code updated' : 'Promo code created');
        setIsDialogOpen(false);
        resetForm();
        fetchPromoCodes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save promo code');
      }
    } catch (error) {
      toast.error('Failed to save promo code');
    }
  };

  const toggleActive = async (code) => {
    try {
      await fetch(`${API_URL}/api/admin/promo-codes/${code.code_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ is_active: !code.is_active })
      });
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to update promo code');
    }
  };

  const deleteCode = async (codeId) => {
    if (!confirm('Are you sure you want to delete this promo code?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/promo-codes/${codeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      toast.success('Promo code deleted');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: '',
      min_order_amount: '',
      max_uses: '',
      max_uses_per_user: '1',
      valid_until: ''
    });
    setEditingCode(null);
  };

  const openEdit = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      description: code.description,
      discount_type: code.discount_type,
      discount_value: code.discount_value.toString(),
      min_order_amount: code.min_order_amount?.toString() || '',
      max_uses: code.max_uses?.toString() || '',
      max_uses_per_user: code.max_uses_per_user?.toString() || '1',
      valid_until: code.valid_until?.split('T')[0] || ''
    });
    setIsDialogOpen(true);
  };

  const openVoucherEdit = (v) => {
    setEditingVoucher(v);
    setVoucherEditForm({
      discount_value: (v.value ?? '').toString(),
      valid_until: v.valid_until ? v.valid_until.split('T')[0] : '',
      is_active: v.is_active !== false
    });
  };

  const handleSaveVoucher = async (e) => {
    e.preventDefault();
    if (!editingVoucher?.code_id) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/promo-codes/${editingVoucher.code_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({
          discount_value: parseFloat(voucherEditForm.discount_value),
          valid_until: voucherEditForm.valid_until || null,
          is_active: voucherEditForm.is_active
        })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to update voucher');
      }
      toast.success('Voucher updated');
      setEditingVoucher(null);
      fetchVouchers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const deleteVoucher = async (codeId) => {
    if (!codeId) return;
    if (!window.confirm('Delete this voucher? This cannot be undone.')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/promo-codes/${codeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to delete voucher');
      toast.success('Voucher deleted');
      fetchVouchers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div data-testid="admin-promo-codes">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Tag className="h-6 w-6 text-primary" />
            Promo Codes
          </h1>
          <p className="text-slate-500 mt-1">Manage discount codes and promotions</p>
        </div>
        <div className="flex gap-2">
        <Dialog open={isVoucherDialogOpen} onOpenChange={(open) => { setIsVoucherDialogOpen(open); if (!open) setGeneratedVouchers([]); }}>
          <DialogTrigger asChild>
            <Button variant="outline" className="border-primary text-primary hover:bg-primary/5" data-testid="generate-voucher-btn">
              <Gift className="h-4 w-4 mr-2" />
              Generate Vouchers
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Gift className="h-5 w-5 text-primary" /> Generate Gift Vouchers</DialogTitle>
            </DialogHeader>
            {generatedVouchers.length === 0 ? (
              <form onSubmit={handleGenerateVouchers} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Voucher Value (R) *</Label>
                    <Input type="number" min="10" value={voucherForm.value}
                      onChange={(e) => setVoucherForm(prev => ({ ...prev, value: e.target.value }))}
                      placeholder="100" required data-testid="voucher-value-input" />
                  </div>
                  <div>
                    <Label>Quantity *</Label>
                    <Input type="number" min="1" max="100" value={voucherForm.quantity}
                      onChange={(e) => setVoucherForm(prev => ({ ...prev, quantity: e.target.value }))}
                      placeholder="1" required data-testid="voucher-quantity-input" />
                  </div>
                </div>
                <div>
                  <Label>Note (optional)</Label>
                  <Input value={voucherForm.note}
                    onChange={(e) => setVoucherForm(prev => ({ ...prev, note: e.target.value }))}
                    placeholder="Internal note e.g. Staff appreciation" />
                </div>
                <div>
                  <Label>Message / Occasion on voucher (optional)</Label>
                  <Input value={voucherForm.message} maxLength={120}
                    onChange={(e) => setVoucherForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="e.g. Happy Birthday — enjoy lunch on us!" data-testid="voucher-message-input" />
                  <p className="text-xs text-slate-400 mt-1">Printed on the voucher itself. Shared across all vouchers in this batch.</p>
                </div>
                <div>
                  <Label>Valid Until (optional)</Label>
                  <Input type="date" value={voucherForm.valid_until}
                    onChange={(e) => setVoucherForm(prev => ({ ...prev, valid_until: e.target.value }))} />
                </div>
                <p className="text-xs text-slate-500">Each voucher is a single-use, Rand-value code with a unique QR that links to the login/sign-up page. When redeemed, its full value is loaded into the customer's wallet.</p>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsVoucherDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={generating} className="bg-primary hover:bg-primary-600" data-testid="confirm-generate-voucher">
                    {generating ? 'Generating...' : 'Generate'}
                  </Button>
                </DialogFooter>
              </form>
            ) : (
              <div className="space-y-4" data-testid="generated-vouchers">
                <p className="text-sm text-slate-600">{generatedVouchers.length} voucher(s) ready. Download or email the branded PDF, or copy the codes.</p>
                <div className="rounded-xl overflow-hidden border bg-slate-50 flex justify-center" style={{ height: 245 }}>
                  <div style={{ transform: 'scale(0.55)', transformOrigin: 'top center' }}>
                    <VoucherCard {...vouchersToCards(generatedVouchers)[0]} />
                  </div>
                </div>
                {generatedVouchers.length > 1 && (
                  <p className="text-xs text-center text-slate-400 -mt-2">Preview of voucher 1 of {generatedVouchers.length}. The PDF includes one voucher per page.</p>
                )}
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {generatedVouchers.map((v) => (
                    <div key={v.code} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                      <span className="font-mono font-bold text-primary text-sm">{v.code}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold">R{v.value}</span>
                        <button onClick={() => { navigator.clipboard?.writeText(v.code); toast.success('Copied'); }} className="text-slate-400 hover:text-primary" title="Copy code">
                          <Copy className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDownloadOneVoucher(v)} disabled={voucherPdfBusy} className="text-slate-400 hover:text-primary" title="Download this voucher" data-testid={`download-one-${v.code}`}>
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" variant="outline" onClick={handleDownloadAllVouchers} disabled={voucherPdfBusy} data-testid="download-all-vouchers">
                    <Download className="h-4 w-4 mr-2" /> {voucherPdfBusy ? 'Working...' : 'Download all (PDF)'}
                  </Button>
                  <button type="button"
                    onClick={() => { navigator.clipboard?.writeText(generatedVouchers.map(v => `${v.code} - R${v.value}`).join('\n')); toast.success('All codes copied'); }}
                    className="text-sm text-primary font-medium border rounded-md hover:bg-primary/5">Copy all codes</button>
                </div>
                <div className="border-t pt-3">
                  <Label className="text-xs">Email the PDF to a client</Label>
                  <div className="flex gap-2 mt-1">
                    <Input type="email" value={voucherEmail} onChange={(e) => setVoucherEmail(e.target.value)} placeholder="client@email.com" data-testid="voucher-email-input" />
                    <Button type="button" onClick={handleEmailVouchers} disabled={voucherPdfBusy} variant="outline" data-testid="email-vouchers-btn">
                      <Mail className="h-4 w-4 mr-1" /> Email
                    </Button>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" onClick={() => { setGeneratedVouchers([]); setVoucherEmail(''); setVoucherForm(prev => ({ ...prev, message: '' })); setIsVoucherDialogOpen(false); }} className="bg-primary hover:bg-primary-600">Done</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary-600" data-testid="add-promo-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Promo Code
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>{editingCode ? 'Edit' : 'Create'} Promo Code</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Code *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    placeholder="e.g., SAVE20"
                    className="uppercase"
                    required
                    disabled={!!editingCode}
                    data-testid="promo-code-input"
                  />
                </div>
                <div>
                  <Label>Discount Type</Label>
                  <Select value={formData.discount_type} onValueChange={(v) => setFormData(prev => ({ ...prev, discount_type: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                      <SelectItem value="fixed">Fixed Amount (R)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="20% off your order"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Discount Value *</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      value={formData.discount_value}
                      onChange={(e) => setFormData(prev => ({ ...prev, discount_value: e.target.value }))}
                      placeholder={formData.discount_type === 'percentage' ? '20' : '50'}
                      required
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                      {formData.discount_type === 'percentage' ? '%' : 'R'}
                    </span>
                  </div>
                </div>
                <div>
                  <Label>Min Order Amount (R)</Label>
                  <Input
                    type="number"
                    value={formData.min_order_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, min_order_amount: e.target.value }))}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Max Total Uses</Label>
                  <Input
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_uses: e.target.value }))}
                    placeholder="Unlimited"
                  />
                </div>
                <div>
                  <Label>Per User Limit</Label>
                  <Input
                    type="number"
                    value={formData.max_uses_per_user}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_uses_per_user: e.target.value }))}
                    placeholder="1"
                  />
                </div>
                <div>
                  <Label>Valid Until</Label>
                  <Input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData(prev => ({ ...prev, valid_until: e.target.value }))}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary-600">
                  {editingCode ? 'Update' : 'Create'} Code
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Invite & Earn — Referral Program config */}
      <Card className="mb-6 border-primary/20" data-testid="referral-config-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><Users className="h-5 w-5 text-primary" /> Invite &amp; Earn — Referral Program</CardTitle>
          <CardDescription>Set what a customer earns for inviting a friend, and the minimum the friend must spend to qualify. Rewards are paid automatically into the referrer's wallet.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveReferral} className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div>
              <Label>Referrer earns (R)</Label>
              <Input type="number" min="0" step="1" value={referralConfig.referrer_reward}
                onChange={(e) => setReferralConfig(prev => ({ ...prev, referrer_reward: e.target.value }))}
                data-testid="referral-referrer-reward" />
              <p className="text-xs text-slate-400 mt-1">Credited to the inviter's wallet.</p>
            </div>
            <div>
              <Label>New customer earns (R)</Label>
              <Input type="number" min="0" step="1" value={referralConfig.referee_reward}
                onChange={(e) => setReferralConfig(prev => ({ ...prev, referee_reward: e.target.value }))}
                data-testid="referral-referee-reward" />
              <p className="text-xs text-slate-400 mt-1">Set 0 if only the inviter earns.</p>
            </div>
            <div>
              <Label>Qualifying spend (R)</Label>
              <Input type="number" min="0" step="1" value={referralConfig.min_spend}
                onChange={(e) => setReferralConfig(prev => ({ ...prev, min_spend: e.target.value }))}
                data-testid="referral-min-spend" />
              <p className="text-xs text-slate-400 mt-1">Friend's first order must reach this total.</p>
            </div>
            <div className="sm:col-span-3 border-t pt-4 mt-1">
              <p className="text-sm font-semibold flex items-center gap-1.5"><span>🍽️</span> Free-lunch milestone</p>
              <p className="text-xs text-slate-400 mb-3">Reward loyal referrers with a free lunch every time enough of their invitees have ordered. Set threshold to 0 to disable.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Referrals per free lunch</Label>
                  <Input type="number" min="0" step="1" value={referralConfig.milestone_threshold}
                    onChange={(e) => setReferralConfig(prev => ({ ...prev, milestone_threshold: e.target.value }))}
                    data-testid="referral-milestone-threshold" />
                  <p className="text-xs text-slate-400 mt-1">e.g. 3 = "Refer 3, get a free lunch". Counts only friends who placed a qualifying order.</p>
                </div>
                <div>
                  <Label>Free-lunch reward (R)</Label>
                  <Input type="number" min="0" step="1" value={referralConfig.milestone_reward}
                    onChange={(e) => setReferralConfig(prev => ({ ...prev, milestone_reward: e.target.value }))}
                    data-testid="referral-milestone-reward" />
                  <p className="text-xs text-slate-400 mt-1">Added automatically to the referrer's wallet, repeatable.</p>
                </div>
              </div>
            </div>
            <div className="sm:col-span-3 flex items-center justify-between gap-3 pt-1">
              <p className="text-xs text-slate-500">
                Currently: invite a friend → they spend <strong>R{referralConfig.min_spend || 0}+</strong> on their first order → you earn <strong>R{referralConfig.referrer_reward || 0}</strong> (once per friend), paid straight to your wallet.
              </p>
              <Button type="submit" disabled={savingReferral} className="bg-primary hover:bg-primary-600" data-testid="save-referral-config">
                {savingReferral ? 'Saving...' : 'Save Referral Settings'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Promo Codes List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="h-24" />
            </Card>
          ))}
        </div>
      ) : promoCodes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Tag className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No promo codes yet</p>
            <p className="text-sm text-slate-400">Create your first promo code to start offering discounts</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {promoCodes.map((code) => (
            <Card key={code.code_id || code.code} className={!code.is_active ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-primary/10 p-3 rounded-lg">
                      {code.discount_type === 'percentage' ? (
                        <Percent className="h-6 w-6 text-primary" />
                      ) : (
                        <DollarSign className="h-6 w-6 text-primary" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-lg">{code.code}</span>
                        <Badge variant={code.is_active ? 'default' : 'secondary'}>
                          {code.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500">{code.description}</p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                        <span>
                          {code.discount_type === 'percentage' ? `${code.discount_value}% off` : `R${code.discount_value} off`}
                        </span>
                        {code.min_order_amount > 0 && (
                          <span>Min: R{code.min_order_amount}</span>
                        )}
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {code.uses_count} / {code.max_uses || '∞'} used
                        </span>
                        {code.valid_until && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Expires: {new Date(code.valid_until).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={code.is_active}
                      onCheckedChange={() => toggleActive(code)}
                    />
                    <Button variant="ghost" size="sm" onClick={() => openEdit(code)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteCode(code.code_id)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Vouchers List */}
      <div className="mt-10" data-testid="vouchers-section">
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-1">
          <Gift className="h-5 w-5 text-primary" /> Gift Vouchers
        </h2>
        <p className="text-slate-500 mb-4 text-sm">Single-use Rand-value codes generated by staff or purchased by customers.</p>
        {vouchers.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <Gift className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No vouchers yet</p>
              <p className="text-sm text-slate-400">Generate vouchers above to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {vouchers.map((v) => (
              <Card key={v.code_id || v.code} className={(v.redeemed || v.is_active === false) ? 'opacity-60' : ''} data-testid={`voucher-card-${v.code}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono font-bold text-primary">{v.code}</span>
                    <Badge variant={v.redeemed ? 'secondary' : (v.is_active === false ? 'outline' : 'default')}>
                      {v.redeemed ? 'Redeemed' : (v.is_active === false ? 'Inactive' : 'Active')}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm text-slate-500">
                    <span className="font-semibold text-slate-700">R{v.value}</span>
                    <span className="text-xs capitalize">{v.source === 'purchase' ? 'Purchased' : 'Staff'}</span>
                  </div>
                  {v.valid_until && (
                    <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> Expires: {new Date(v.valid_until).toLocaleDateString()}
                    </p>
                  )}
                  {v.note && <p className="text-xs text-slate-400 mt-1">{v.note}</p>}
                  {!v.redeemed && v.code_id && (
                    <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t">
                      <Button variant="ghost" size="sm" onClick={() => openVoucherEdit(v)} data-testid={`edit-voucher-${v.code}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteVoucher(v.code_id)} className="text-red-500 hover:text-red-700" data-testid={`delete-voucher-${v.code}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Voucher Dialog */}
      <Dialog open={!!editingVoucher} onOpenChange={(open) => { if (!open) setEditingVoucher(null); }}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Gift className="h-5 w-5 text-primary" /> Edit Voucher {editingVoucher?.code}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveVoucher} className="space-y-4">
            <div>
              <Label>Voucher Value (R) *</Label>
              <Input type="number" min="1" value={voucherEditForm.discount_value}
                onChange={(e) => setVoucherEditForm(prev => ({ ...prev, discount_value: e.target.value }))}
                required data-testid="voucher-edit-value" />
            </div>
            <div>
              <Label>Valid Until (optional)</Label>
              <Input type="date" value={voucherEditForm.valid_until}
                onChange={(e) => setVoucherEditForm(prev => ({ ...prev, valid_until: e.target.value }))}
                data-testid="voucher-edit-expiry" />
              <p className="text-xs text-slate-400 mt-1">Leave empty for no expiry. Valid through the end of the chosen day.</p>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="voucher-active">Active</Label>
              <Switch id="voucher-active" checked={voucherEditForm.is_active}
                onCheckedChange={(c) => setVoucherEditForm(prev => ({ ...prev, is_active: c }))}
                data-testid="voucher-edit-active" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditingVoucher(null)}>Cancel</Button>
              <Button type="submit" className="bg-primary hover:bg-primary-600" data-testid="voucher-edit-save">Save Voucher</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
